import { type FormEvent, useEffect, useState } from "react";
import {
  type DepartmentDTO,
  createDepartment,
  deleteDepartment,
  getUsersApi,
  listDepartments,
  updateDepartment,
} from "@praesto/core";
import { Modal, Button, Input, FormField, Checkbox } from "@praesto/ui-kit";

const SLOTS = ["M", "P", "N", "X"] as const;
type Slot = (typeof SLOTS)[number];

export function DepartmentModal(props: { hotelId: string; onClose: () => void }) {
  const [departments, setDepartments] = useState<ReadonlyArray<DepartmentDTO>>([]);
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [shifts, setShifts] = useState<ReadonlyArray<Slot>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  // Inline edit state: the code being edited (null = none) + its draft fields.
  const [editCode, setEditCode] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editShifts, setEditShifts] = useState<ReadonlyArray<Slot>>([]);

  useEffect(() => {
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listDepartments(api.apiBase, props.hotelId, tok);
      setDepartments(r.departments);
    })().catch((e) => setError(String(e)));
  }, [props.hotelId, reload]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await createDepartment(api.apiBase, props.hotelId, { code, displayName, shifts }, tok);
      setCode("");
      setDisplayName("");
      setShifts([]);
      setReload((r) => r + 1);
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function disable(c: string) {
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await deleteDepartment(api.apiBase, props.hotelId, c, tok);
      setReload((r) => r + 1);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(d: DepartmentDTO) {
    setEditCode(d.code);
    setEditName(d.displayName);
    setEditShifts(d.shifts);
    setError(null);
  }

  async function saveEdit() {
    if (!editCode) return;
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateDepartment(
        api.apiBase,
        props.hotelId,
        editCode,
        { displayName: editName, shifts: editShifts },
        tok,
      );
      setEditCode(null);
      setReload((r) => r + 1);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reactivate(c: string) {
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateDepartment(api.apiBase, props.hotelId, c, { status: "active" }, tok);
      setReload((r) => r + 1);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={props.onClose} title={`Reparti · ${props.hotelId}`}>
      <div className="space-y-4">
        <ul className="space-y-1">
          {departments.map((d) =>
            editCode === d.code ? (
              <li
                key={d.code}
                className="border border-border-strong px-2 py-2 rounded text-sm space-y-2"
              >
                <div className="font-mono text-mono-xs text-fg-muted">{d.code}</div>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  aria-label="Nome reparto"
                />
                <div className="flex flex-wrap gap-3">
                  {SLOTS.map((s) => (
                    <Checkbox
                      key={s}
                      label={s}
                      checked={editShifts.includes(s)}
                      onChange={(e) =>
                        setEditShifts(
                          e.target.checked ? [...editShifts, s] : editShifts.filter((x) => x !== s),
                        )
                      }
                    />
                  ))}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditCode(null)}>
                    Annulla
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={saveEdit}
                    loading={busy}
                    disabled={busy || !editName}
                  >
                    Salva
                  </Button>
                </div>
              </li>
            ) : (
              <li
                key={d.code}
                className="border border-border-default px-2 py-1 rounded text-sm flex items-center justify-between gap-2"
              >
                <span className="min-w-0">
                  {d.displayName}{" "}
                  <span className="font-mono text-mono-xs text-fg-muted">({d.code})</span>
                  {d.status === "disabled" && " · disabilitato"}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {d.status === "active" ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(d)}>
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-danger"
                        onClick={() => disable(d.code)}
                      >
                        Disabilita
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-success"
                      onClick={() => reactivate(d.code)}
                      disabled={busy}
                    >
                      Riattiva
                    </Button>
                  )}
                </span>
              </li>
            ),
          )}
        </ul>
        <form onSubmit={submit} className="space-y-2 border-t border-border-default pt-3">
          <FormField label="Codice (snake_case)" required>
            <Input
              value={code}
              required
              pattern="^[a-z][a-z0-9_]*$"
              onChange={(e) => setCode(e.target.value)}
            />
          </FormField>
          <FormField label="Nome visibile" required>
            <Input value={displayName} required onChange={(e) => setDisplayName(e.target.value)} />
          </FormField>
          <FormField label="Slot turni">
            <div className="flex flex-wrap gap-3">
              {SLOTS.map((s) => (
                <Checkbox
                  key={s}
                  label={s}
                  checked={shifts.includes(s)}
                  onChange={(e) =>
                    setShifts(e.target.checked ? [...shifts, s] : shifts.filter((x) => x !== s))
                  }
                />
              ))}
            </div>
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={props.onClose}
            >
              Chiudi
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              loading={busy}
              disabled={busy || !code || !displayName}
            >
              Aggiungi
            </Button>
          </div>
        </form>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
